import React, { useMemo } from 'react';
import styled from 'styled-components';
import { RouteComponentProps } from 'react-router-dom';
import { media } from '../theme/tokens';
import { KpiStrip } from '../components/KpiStrip';
import { DataTable } from '../components/DataTable';
import { SelectDropdown } from '../components/SelectDropdown';
import { PageHeader } from '../components/PageHeader';
import { TabBar } from '../components/TabBar';
import { useWebBasic } from 'apiTyped/hooks/onlineSales.query';

// ─── Types ───────────────────────────────────────────────────────────────────

interface StoreReference {
  id?: string | number;
  store_name: string;
  market_name: string;
  nb_references: number;
  avg_price: number;
  min_price: number;
  max_price: number;
}

interface DnHistoryPoint {
  date: string;
  wine_name: string;
  market_name: string;
  dn: number;
}

// ─── Component ───────────────────────────────────────────────────────────────

export const OnlineSalesOverview: React.FC<RouteComponentProps> = ({
  history
}) => {
  const [selectedWine, setSelectedWine] = React.useState<string>('');
  const [selectedMarket, setSelectedMarket] = React.useState<string>('');

  const { isLoading, webBasicData } = useWebBasic({
    wines: selectedWine || undefined,
    market: selectedMarket || undefined
  });

  const {
    kpis = [],
    feed = [],
    prices = [],
    store_references = [],
    dn_history = []
  } = webBasicData as Record<string, any>;

  // Derive filter options from data
  const wineOptions = useMemo(() => {
    const filters = (webBasicData as any)?.filters || {};
    const clientWines: { wine_name: string }[] = filters.client_wines || [];
    return clientWines.map((w) => ({ value: w.wine_name, label: w.wine_name }));
  }, [webBasicData]);

  const marketOptions = useMemo(() => {
    const filters = (webBasicData as any)?.filters || {};
    const markets: string[] = filters.markets || [];
    return markets.map((m) => ({ value: m, label: m }));
  }, [webBasicData]);

  // KPI aggregation
  const totalDn = useMemo(() => {
    if (!kpis.length) return 0;
    return kpis.reduce((sum: number, k: any) => sum + (k.dn || 0), 0);
  }, [kpis]);

  const totalStores = store_references.length;

  const avgPrice = useMemo(() => {
    if (!prices.length) return '—';
    const avg =
      prices.reduce((s: number, p: any) => s + (p.price || 0), 0) /
      prices.length;
    return `€${avg.toFixed(0)}`;
  }, [prices]);

  const latestDate = useMemo(() => {
    if (!kpis.length) return '';
    return kpis[0]?.date || '';
  }, [kpis]);

  // DN history chart data (simplified: show latest per market)
  const dnByMarket = useMemo(() => {
    const map = new Map<string, number>();
    (dn_history as DnHistoryPoint[]).forEach((d) => {
      map.set(d.market_name, d.dn);
    });
    return Array.from(map.entries()).map(([market, dn]) => ({
      id: market,
      market,
      dn
    }));
  }, [dn_history]);

  // Points of sale table columns
  const posColumns = useMemo(
    () => [
      {
        key: 'store_name',
        header: 'Store',
        render: (row: StoreReference) => (
          <StoreName>{row.store_name}</StoreName>
        ),
        width: '25%'
      },
      {
        key: 'market_name',
        header: 'Market',
        render: (row: StoreReference) => row.market_name
      },
      {
        key: 'nb_references',
        header: 'References',
        render: (row: StoreReference) => row.nb_references,
        sortable: true
      },
      {
        key: 'avg_price',
        header: 'Avg. Price',
        render: (row: StoreReference) => `€${(row.avg_price || 0).toFixed(0)}`,
        sortable: true
      },
      {
        key: 'min_price',
        header: 'Min Price',
        render: (row: StoreReference) => `€${(row.min_price || 0).toFixed(0)}`
      },
      {
        key: 'max_price',
        header: 'Max Price',
        render: (row: StoreReference) => `€${(row.max_price || 0).toFixed(0)}`
      }
    ],
    []
  );

  // DN table columns
  const dnColumns = useMemo(
    () => [
      {
        key: 'market',
        header: 'Market',
        render: (row: { id: string; market: string; dn: number }) => row.market
      },
      {
        key: 'dn',
        header: 'Digital Numeric (DN)',
        render: (row: { id: string; market: string; dn: number }) => row.dn,
        sortable: true
      }
    ],
    []
  );

  return (
    <Page>
      {/* Header */}
      <PageHeader
        title="Online Sales"
        subtitle="Monitor your wines' digital distribution across online retailers — track listings, pricing, and market coverage."
      />

      {/* Tabs */}
      <TabBar
        tabs={[
          { id: 'overview', label: 'Overview' },
          { id: 'detailed', label: 'Detailed' }
        ]}
        activeTab="overview"
        onTabChange={(id) => {
          if (id === 'detailed')
            history.push('/v2/campaigns/online-sales/detailed');
        }}
        meta={latestDate ? <>Last updated: {latestDate}</> : undefined}
      />

      {/* Filters */}
      <FiltersRow>
        <FilterPill>
          <PillLabel>Wine</PillLabel>
          <SelectDropdown
            options={[{ value: '', label: 'All wines' }, ...wineOptions]}
            value={selectedWine}
            onChange={(val) => setSelectedWine(val)}
            disabled={isLoading}
          />
        </FilterPill>
        <FilterPill>
          <PillLabel>Market</PillLabel>
          <SelectDropdown
            options={[{ value: '', label: 'All markets' }, ...marketOptions]}
            value={selectedMarket}
            onChange={(val) => setSelectedMarket(val)}
            disabled={isLoading}
          />
        </FilterPill>
      </FiltersRow>

      {/* KPI Strip */}
      <KpiStrip
        items={[
          {
            label: 'Digital Distribution (DN)',
            value: totalDn.toLocaleString(),
            delta: { value: 'Current period', direction: 'flat' }
          },
          {
            label: 'Online Stores',
            value: totalStores.toLocaleString(),
            delta: { value: 'Active retailers', direction: 'flat' }
          },
          {
            label: 'Average Price',
            value: avgPrice,
            delta: { value: 'Across all references', direction: 'flat' }
          },
          {
            label: 'Wines Tracked',
            value: wineOptions.length.toString(),
            delta: { value: 'Client portfolio', direction: 'flat' }
          }
        ]}
      />

      {isLoading && <LoadingBar>Loading data…</LoadingBar>}

      {/* DN by Market */}
      {dnByMarket.length > 0 && (
        <Section>
          <SectionHeader>
            <SectionTitle>DN by Market</SectionTitle>
            <SectionMeta>Digital numeric distribution per market</SectionMeta>
          </SectionHeader>
          <BarChartGrid>
            {dnByMarket.map((item) => (
              <BarItem key={item.market}>
                <BarLabel>{item.market}</BarLabel>
                <BarTrack>
                  <BarFill
                    style={{
                      width: `${Math.min(
                        100,
                        (item.dn / Math.max(...dnByMarket.map((d) => d.dn))) *
                          100
                      )}%`
                    }}
                  />
                </BarTrack>
                <BarValue>{item.dn}</BarValue>
              </BarItem>
            ))}
          </BarChartGrid>
        </Section>
      )}

      {/* Points of Sale table */}
      <Section>
        <SectionHeader>
          <SectionTitle>Online Retailers</SectionTitle>
          <SectionMeta>{totalStores} stores listing your wines</SectionMeta>
        </SectionHeader>
        <DataTable
          columns={posColumns}
          data={(store_references as StoreReference[]).map((s, i) => ({
            ...s,
            id: s.id ?? i
          }))}
          emptyMessage="No retailer data for the current selection."
        />
      </Section>
    </Page>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const Page = styled.div`
  padding: 0;
`;

const FiltersRow = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const FilterPill = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PillLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: var(--ink-muted);
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const LoadingBar = styled.div`
  text-align: center;
  padding: 24px;
  color: var(--ink-muted);
  font-size: 13px;
`;

const Section = styled.div`
  margin-bottom: 32px;
`;

const SectionHeader = styled.div`
  margin-bottom: 12px;
`;

const SectionTitle = styled.h3`
  font-family: var(--font-sans-bold);
  font-size: 16px;
  font-weight: 600;
  color: var(--ink);
  margin: 0 0 2px;
`;

const SectionMeta = styled.p`
  font-size: 12px;
  color: var(--ink-faint);
  margin: 0;
`;

const StoreName = styled.span`
  font-weight: 600;
  color: var(--ink);
`;

// Simple horizontal bar chart
const BarChartGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: var(--white);
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  padding: 16px;
`;

const BarItem = styled.div`
  display: grid;
  grid-template-columns: 120px 1fr 50px;
  align-items: center;
  gap: 12px;
`;

const BarLabel = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: var(--ink);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const BarTrack = styled.div`
  height: 8px;
  background: var(--paper-warm);
  border-radius: 4px;
  overflow: hidden;
`;

const BarFill = styled.div`
  height: 100%;
  background: var(--terracotta);
  border-radius: 4px;
  transition: width 0.3s ease;
`;

const BarValue = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: var(--ink);
  text-align: right;
`;
